/**
 * Safari bfcache image-recovery hook
 *
 * Problem: When iPhone/iPad Safari restores a page from bfcache (the user
 * backgrounds the tab for 30+ minutes, then returns), some `<img>` elements
 * can paint as blank/empty even though their `src` is intact. This is a
 * documented WebKit quirk — the image's decoded bitmap is dropped under
 * memory pressure while backgrounded, and on restore the bitmap is not
 * automatically re-decoded.
 *
 * Fix: On `pageshow` with `event.persisted === true` (= bfcache restore),
 * walk every `<img>` on the page. If the image reports `complete === true`
 * but `naturalWidth === 0` (= bitmap was dropped) OR is below-the-fold and
 * may have been decode-evicted, re-trigger the decode by toggling `src`
 * once. Lightweight — costs only a few microseconds per image on resume.
 *
 * Also listens to `visibilitychange` for the same recovery on tab unhide
 * without bfcache (e.g., user switched tabs and came back). Throttled so
 * it runs at most once per 5 seconds.
 */
let lastRecoveryAt = 0;

function recoverImages() {
  const now = Date.now();
  if (now - lastRecoveryAt < 5000) return;
  lastRecoveryAt = now;

  const imgs = document.querySelectorAll('img');
  imgs.forEach((img) => {
    // Skip if it never had a src or is a tiny tracking pixel
    if (!img.src || img.width < 4 || img.height < 4) return;

    // We ONLY want to recover images that were previously loaded and then
    // had their decoded bitmap evicted under memory pressure (the real
    // bfcache-restore + Safari-blank-image bug). The discriminator is:
    //   • complete === true   → the HTTP fetch succeeded once
    //   • naturalWidth === 0  → the decoded bitmap is gone
    //
    // Lazy-not-yet-loaded images have complete=false, so this leaves
    // them alone (the browser's lazy observer will fetch when needed).
    // Loaded-and-healthy images have naturalWidth>0, also left alone.
    if (!img.complete) return;
    if (img.naturalWidth > 0) return;

    // Force re-decode by re-assigning the same src (cheap, hits HTTP cache)
    const src = img.src;
    img.src = '';
    img.src = src;
  });
}

export function installBfcacheImageRecovery() {
  if (typeof window === 'undefined') return;

  // Primary: bfcache restore
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) recoverImages();
  });

  // Secondary: tab visibility change (common on mobile when app switching)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') recoverImages();
  });
}
